import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Crown, CreditCard, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionAccess } from '@/hooks/useSubscriptionAccess';

interface SubscriptionRequiredProps {
  children: React.ReactNode;
  feature?: string; // Name of the feature being blocked
  showOverlay?: boolean; // Whether to show overlay or replace content entirely
}

export function SubscriptionRequired({ 
  children, 
  feature = 'this feature',
  showOverlay = true 
}: SubscriptionRequiredProps) {
  const navigate = useNavigate();
  const { hasAccess, hasAssignedPlan, needsPayment, isLoading } = useSubscriptionAccess();

  // Show children while loading to avoid flicker
  if (isLoading) {
    return <>{children}</>;
  }

  // If user has access, show children normally
  if (hasAccess) {
    return <>{children}</>;
  }

  // If user needs to pay, show subscription required UI
  const handleActivateSubscription = () => {
    navigate('/dashboard/billing?tab=subscription-plans');
  };

  const SubscriptionRequiredContent = (
    <div className="flex items-center justify-center min-h-[400px] p-8">
      <Card className="max-w-lg mx-auto text-center">
        <CardHeader>
          <div className="mx-auto w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Crown className="w-8 h-8 text-purple-600" />
          </div>
          <CardTitle className="text-xl">Subscription Required</CardTitle>
          <CardDescription>
            {hasAssignedPlan 
              ? `Your partner has assigned you a subscription plan. Activate it to access ${feature}.`
              : `You need an active subscription to access ${feature}.`
            }
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasAssignedPlan ? (
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center space-x-2 text-purple-800">
                <Crown className="w-4 h-4" />
                <span className="text-sm font-medium">Plan ready for activation</span>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center space-x-2 text-blue-800">
                <CreditCard className="w-4 h-4" />
                <span className="text-sm font-medium">Choose a subscription plan</span>
              </div>
            </div>
          )}
          
          <Button 
            onClick={handleActivateSubscription}
            className="w-full"
            size="lg"
          >
            {hasAssignedPlan ? (
              <>
                <Crown className="w-4 h-4 mr-2" />
                Activate Your Plan
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                View Subscription Plans
              </>
            )}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>

          <p className="text-xs text-muted-foreground">
            Once activated, you'll have full access to all AI Employee features
          </p>
        </CardContent>
      </Card>
    </div>
  );

  // If showOverlay is true, show content with overlay
  if (showOverlay && needsPayment) {
    return (
      <div className="relative">
        {/* Dimmed background content */}
        <div className="opacity-30 pointer-events-none">
          {children}
        </div>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-background/80 backdrop-blur-sm">
          {SubscriptionRequiredContent}
        </div>
      </div>
    );
  }

  // Otherwise, replace content entirely
  return SubscriptionRequiredContent;
}